"use client";

/**
 * AdminDataContext
 *
 * Eagerly fetches and caches slow-changing admin resources on mount:
 *   - providers   (fetched once)
 *   - schedules   (fetched for the first provider once providers resolve)
 *   - clinic      (fetched once)
 *
 * The hooks useProviders (admin mode), useSchedules (admin mode),
 * useAdminBlockTimes, and useClinicInfo read from this context automatically
 * when rendered inside the admin layout.
 *
 * After any mutation call the appropriate refresh method to reload from server:
 *
 *   const ctx = useAdminData();
 *   await patchProvider(id, changes);
 *   ctx.refreshProviders();
 *
 *   await postSchedule(data);
 *   ctx.refreshSchedules(providerId);
 *
 *   await deleteBlockTime(id);
 *   ctx.refreshBlockTimes(providerId);
 *
 *   await patchClinic(data);
 *   ctx.refreshClinic();
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ProviderRow } from "@/app/types/Provider";
import { Me } from "@/app/types/Staff";
import { Schedule } from "@/app/types/Schedule";
import { ClinicRow } from "@/app/types/Clinic";
import { adminRoutes } from "@/app/lib/api-client/adminRoutes";
import { apiFetch } from "@/app/lib/api-client/base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminDataContextValue {
  // ── Current user ──────────────────────────────────────────────────────
  /** Identity + permissions, resolved server-side. Null until loaded. */
  me: Me | null;
  loadingMe: boolean;
  /**
   * Re-read the caller's identity. Needed whenever something changes what
   * they may do — switching plan changes `tier`, and with it the staff tab
   * and every Plus-only field.
   */
  refreshMe: () => void;

  // ── Providers ─────────────────────────────────────────────────────────
  providers: ProviderRow[];
  loadingProviders: boolean;
  providersError: string | null;
  refreshProviders: () => void;

  /**
   * Whose calendar the admin UI is currently showing. Defaults to your own row
   * if you are a provider, otherwise the first provider in the clinic — so a
   * receptionist or a paperwork-only admin still lands somewhere sensible.
   */
  selectedProviderId: string;
  setSelectedProviderId: (providerId: string) => void;

  // ── Schedules (keyed by providerId) ───────────────────────────────────
  schedules: Record<string, Schedule[]>;
  loadingSchedules: Record<string, boolean>;
  schedulesError: Record<string, string | null>;
  refreshSchedules: (providerId: string) => void;

  // ── Clinic ────────────────────────────────────────────────────────────
  clinic: ClinicRow | null;
  loadingClinic: boolean;
  clinicError: string | null;
  refreshClinic: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const AdminDataContext = createContext<AdminDataContextValue | null>(
  null
);

/** Returns the context value, or null when rendered outside AdminDataProvider */
export function useAdminData(): AdminDataContextValue | null {
  return useContext(AdminDataContext);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AdminDataProvider({ children }: { children: React.ReactNode }) {
  // ── Current user ──────────────────────────────────────────────────────
  const [me, setMe] = useState<Me | null>(null);
  const [loadingMe, setLoadingMe] = useState(true);

  const doFetchMe = useCallback(async () => {
    setLoadingMe(true);
    try {
      setMe(await apiFetch(adminRoutes.me.get()));
    } catch {
      setMe(null);
    } finally {
      setLoadingMe(false);
    }
  }, []);

  // ── Providers ─────────────────────────────────────────────────────────
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providersError, setProvidersError] = useState<string | null>(null);

  const doFetchProviders = useCallback(async () => {
    setLoadingProviders(true);
    setProvidersError(null);
    try {
      const response = await apiFetch(adminRoutes.providers.get());
      setProviders(response?.items ?? []);
    } catch (e) {
      setProvidersError(e instanceof Error ? e.message : "An error occurred");
    } finally {
      setLoadingProviders(false);
    }
  }, []);

  const refreshProviders = useCallback(() => {
    doFetchProviders();
  }, [doFetchProviders]);

  // ── Schedules ─────────────────────────────────────────────────────────
  const [schedules, setSchedules] = useState<Record<string, Schedule[]>>({});
  const [loadingSchedules, setLoadingSchedules] = useState<
    Record<string, boolean>
  >({});
  const [schedulesError, setSchedulesError] = useState<
    Record<string, string | null>
  >({});

  const doFetchSchedules = useCallback(async (providerId: string) => {
    setLoadingSchedules((prev) => ({ ...prev, [providerId]: true }));
    setSchedulesError((prev) => ({ ...prev, [providerId]: null }));
    try {
      const result = await apiFetch(adminRoutes.schedules.get(providerId));
      setSchedules((prev) => ({ ...prev, [providerId]: result.items ?? [] }));
    } catch (err) {
      setSchedulesError((prev) => ({
        ...prev,
        [providerId]:
          err instanceof Error ? err.message : "Failed to fetch schedules",
      }));
      setSchedules((prev) => ({ ...prev, [providerId]: [] }));
    } finally {
      setLoadingSchedules((prev) => ({ ...prev, [providerId]: false }));
    }
  }, []);

  const refreshSchedules = useCallback(
    (providerId: string) => {
      doFetchSchedules(providerId);
    },
    [doFetchSchedules]
  );

  // ── Clinic ────────────────────────────────────────────────────────────
  const [clinic, setClinic] = useState<ClinicRow | null>(null);
  const [loadingClinic, setLoadingClinic] = useState(false);
  const [clinicError, setClinicError] = useState<string | null>(null);

  const doFetchClinic = useCallback(async () => {
    setLoadingClinic(true);
    setClinicError(null);
    try {
      const result = await apiFetch(adminRoutes.clinic.get());
      setClinic(result.clinic);
    } catch {
      setClinicError("Nepodarilo sa načítať informácie o klinike");
    } finally {
      setLoadingClinic(false);
    }
  }, []);

  const refreshClinic = useCallback(() => {
    doFetchClinic();
  }, [doFetchClinic]);

  // ── Selected provider ─────────────────────────────────────────────────
  const [selectedProviderId, setRawSelectedProviderId] = useState("");
  // Distinguishes "the default picked this" from "the user picked this".
  // Validity alone cannot: the auto-pick produces a valid id too, so guarding
  // on validity would freeze an early fallback in place forever.
  const [userPicked, setUserPicked] = useState(false);

  const setSelectedProviderId = useCallback((providerId: string) => {
    setUserPicked(true);
    setRawSelectedProviderId(providerId);
  }, []);

  // ── Initial load ──────────────────────────────────────────────────────
  // Step 1: me + providers + clinic in parallel on mount
  useEffect(() => {
    doFetchMe();
    doFetchProviders();
    doFetchClinic();
  }, [doFetchMe, doFetchProviders, doFetchClinic]);

  // Step 2: pick a provider once we know both who is logged in and who exists.
  //
  // Waits for `me` to finish loading before settling. `providers` usually
  // resolves first, and defaulting on it alone lands on providers[0] — the
  // alphabetically first colleague rather than you — with no later chance to
  // correct, since by then the selection is valid.
  useEffect(() => {
    if (providers.length === 0 || loadingMe) return;
    if (userPicked && providers.some((p) => p.id === selectedProviderId)) return;

    // Your own calendar first. The fallback only applies to reception and to
    // admins who hold no schedule, for whom any provider is as good as another.
    const own = me?.isProvider
      ? providers.find((p) => p.id === me.providerId)
      : undefined;
    const next = own?.id ?? providers[0].id;
    if (next !== selectedProviderId) setRawSelectedProviderId(next);
  }, [providers, me, loadingMe, userPicked, selectedProviderId]);

  // Step 3: schedules for whoever is selected.
  useEffect(() => {
    if (!selectedProviderId) return;
    doFetchSchedules(selectedProviderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProviderId]);

  // ── Memoised context value (stable reference between renders) ─────────
  const value = useMemo<AdminDataContextValue>(
    () => ({
      me,
      loadingMe,
      refreshMe: doFetchMe,

      providers,
      loadingProviders,
      providersError,
      refreshProviders,

      selectedProviderId,
      setSelectedProviderId,

      schedules,
      loadingSchedules,
      schedulesError,
      refreshSchedules,

      clinic,
      loadingClinic,
      clinicError,
      refreshClinic,
    }),
    // refresh* callbacks are stable (useCallback with no deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      me,
      loadingMe,
      providers,
      selectedProviderId,
      setSelectedProviderId,
      loadingProviders,
      providersError,
      schedules,
      loadingSchedules,
      schedulesError,
      clinic,
      loadingClinic,
      clinicError,
    ]
  );

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  );
}
